import { useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { queryKeys } from '@/lib/data/keys';
import {
  getScores365Player,
  resolveScores365Athlete,
  type Scores365Player,
} from '@/lib/data/provider';

/**
 * Resolve a player NAME to its 365 athleteId via the resolve RPC. Cached for the
 * whole session (the name→id mapping is effectively static) so reopening a player
 * is instant. Gated on a non-empty name + a configured backend; returns
 * `undefined` while pending and `null` when the name is unmapped.
 */
function useScores365Athlete(playerName?: string) {
  const name = playerName?.trim() ?? '';
  return useQuery({
    queryKey: queryKeys.scores365Athlete(name),
    queryFn: () => resolveScores365Athlete(name),
    // The name→id mapping never changes for a given name, so keep it for the
    // whole session: long stale + infinite gc so the second visit skips the RPC.
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: isSupabaseConfigured && name.length > 0,
  });
}

export interface UseScores365PlayerResult {
  profile: Scores365Player | null;
  isLoading: boolean;
}

/**
 * The RICH 365scores player profile for a display name, in two steps:
 *   1. resolve the name → athleteId (long-cached; the mapping is static),
 *   2. once resolved, fetch the full profile (best-effort; the edge fn never
 *      500s, returning null for an unknown athlete).
 * `isLoading` covers BOTH steps so the caller can show a single skeleton.
 * Returns `{ profile: null }` (not loading) when the name doesn't resolve, so the
 * caller can self-hide the whole section.
 */
export function useScores365Player(playerName?: string): UseScores365PlayerResult {
  const athleteQuery = useScores365Athlete(playerName);
  const athleteId = athleteQuery.data ?? null;

  const profileQuery = useQuery({
    queryKey: queryKeys.scores365Player(athleteId ?? 0),
    queryFn: () => getScores365Player(athleteId as number),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && athleteId != null,
  });

  // Step 1 loading, or step 1 resolved an id and step 2 is still loading.
  const resolving = athleteQuery.isLoading;
  const fetchingProfile = athleteId != null && profileQuery.isLoading;

  return {
    profile: profileQuery.data ?? null,
    isLoading: resolving || fetchingProfile,
  };
}
